import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';

import { AppBarCMS } from '../../../../basic/index.jsx';
import { CollapseBlock, Spinner } from '../../../../common/index.jsx';
import IngProjectsForm from './IngProjectsForm.jsx';
import {
  useGetIngProjectQuery,
  useGetIngProjectsQuery,
  useSaveIngProjectMutation,
} from '../../../../../redux/services/ing_projects.jsx';
import { useGetIngProjectsSettingsQuery } from '../../../../../redux/services/ing_projects_settings.jsx';
import { useGetStudyProgramsForIngProjectsQuery } from '../../../../../redux/services/study_programs.jsx';
import { useGetTeachersQuery } from '../../../../../redux/services/users.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import {
  ROLE_ING_PROJECTS_ADMIN,
  ROLE_ING_PROJECTS_GUARANTOR,
} from '../../../../../utils/constants/roles.js';
import { HttpError } from '../../../../common/HttpError/index.jsx';

const IngProjectsEdit = ({ mode }) => {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();

  const { saveSuccess: showSaveSuccess } = useToastMessages();
  const isAdmin = useUserHasRole([ROLE_ING_PROJECTS_ADMIN]);
  const isIngProjectsGuarantor = useUserHasRole([ROLE_ING_PROJECTS_GUARANTOR]);
  const mustUseOwnGuarantor = isIngProjectsGuarantor && !isAdmin;

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const user_id = useSelector((state) => state.auth.user.id);

  // Fetch settings for modification period check
  const { data: settings } = useGetIngProjectsSettingsQuery();
  const canModifyProjects = settings?.can_modify_projects ?? false;
  const canManageProjects = canModifyProjects || isAdmin;

  // Načítanie študijných programov
  const { data: studyPrograms, isLoading: isLoadingStudyPrograms } = useGetStudyProgramsForIngProjectsQuery();

  // Načítanie učiteľov (garant nemôže byť doktorand)
  const { data: teachers, isLoading: isLoadingTeachers } = useGetTeachersQuery({
    excludeDoktorands: true,
  });

  // Načítanie učiteľov vrátane doktorandov (ďalší učitelia na projekte)
  const {
    data: teachersWithDoktorands,
    isLoading: isLoadingTeachersWithDoktorands,
  } = useGetTeachersQuery();

  // detail projektu – iba pri edite
  const { data: projectDetail, isLoading: isLoadingDetail } =
    useGetIngProjectQuery(projectId, { skip: isCreate });

  const [saveIngProject, { isSuccess: isSaveSuccess, isLoading: isSaving }] =
    useSaveIngProjectMutation();

  const currentTeacherId = useMemo(
    () => {
      if (!teachers) {
        return null;
      }

      const teacher = teachers.find(
        (item) => Number(item.person_id) === Number(user_id)
          || Number(item.id) === Number(user_id),
      );

      return teacher?.id ?? null;
    },
    [teachers, user_id],
  );

  const { refetch } = useGetIngProjectsQuery();

  useEffect(() => {
    if (isSaveSuccess) {
      showSaveSuccess();
      navigate(routes.cmsIngProjects);
      refetch();
    }
  }, [isSaveSuccess, showSaveSuccess, navigate, refetch]);

  // Vytvorenie options pre select študijných programov
  const studyProgramsOptions = useMemo(
    () =>
      studyPrograms?.map((item) => {
        const shortcut = item.study_program_type?.shortcut;
        return {
          value: item.id,
          label: shortcut ? `${item.name} (${shortcut})` : item.name,
        };
      }) || [],
    [studyPrograms],
  );

  const getTeacherDisplayName = (teacher) => {
    const fullName = teacher?.full_name || teacher?.fullName || teacher?.person?.full_name;
    const firstName = teacher?.first_name || teacher?.given_name || teacher?.person?.first_name || '';
    const lastName = teacher?.last_name || teacher?.sn || teacher?.person?.last_name || '';

    if (fullName) {
      return fullName;
    }

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return `${teacher?.given_name || ''} ${teacher?.sn || ''}`.trim() || '–';
  };

  const getTeachersOptions = (teachersList) =>
    teachersList
      ?.slice()
      .sort((left, right) => {
        const leftName = `${left?.last_name || left?.person?.last_name || ''} ${left?.first_name || left?.person?.first_name || ''}`.trim();
        const rightName = `${right?.last_name || right?.person?.last_name || ''} ${right?.first_name || right?.person?.first_name || ''}`.trim();

        return leftName.localeCompare(rightName, 'sk', { sensitivity: 'base' });
      })
      .map((teacher) => ({
        value: teacher.id,
        label: getTeacherDisplayName(teacher),
      })) || [];

  const teachersOptions = useMemo(
    () => getTeachersOptions(teachers),
    [teachers],
  );

  const teachersWithDoktorandsOptions = useMemo(
    () => getTeachersOptions(teachersWithDoktorands),
    [teachersWithDoktorands],
  );

  // Vytvorenie options pre select učiteľov (bez garanta)
  const additionalTeachersOptions = useMemo(
    () => {
      const guarantorId = projectDetail?.guarantor_id;

      return (
        teachersWithDoktorandsOptions
          .filter((teacher) => teacher.value !== guarantorId)
      );
    },
    [teachersWithDoktorandsOptions, projectDetail?.guarantor_id],
  );

  const forcedGuarantorId = mustUseOwnGuarantor ? currentTeacherId : null;

  const initials = useMemo(
    () => ({
      name: undefined,
      shortcut: undefined,
      description: undefined,
      min_students: undefined,
      max_students: undefined,
      notes: undefined,
      active: 'Áno',
      study_program_ids: [],
      teacher_ids: [],
      ...projectDetail,
    }),
    [projectDetail, user_id, isCreate],
  );

  // keď dorazí detail, naplníme form
  useEffect(() => {
    if (isEdit && projectDetail) {
      form.setFieldsValue(initials);
    }
    if (isCreate) {
      form.setFieldsValue(initials);
    }
    if (forcedGuarantorId) {
      form.setFieldValue('guarantor_id', forcedGuarantorId);
    }
  }, [isEdit, isCreate, projectDetail, initials, form, forcedGuarantorId]);

  const title = isCreate
    ? t('Pridanie inžinierskeho projektu')
    : t('Editácia inžinierskeho projektu');

  useEffect(() => {
    document.title = title + ' | FRI UNIZA';
  }, [title]);

  if (isCreate && !isIngProjectsGuarantor) {
    return <HttpError showTitle={false} showShadow={false} statusCode={403} />;
  }

  if (isLoadingStudyPrograms || isLoadingTeachers || isLoadingTeachersWithDoktorands || (isEdit && (isLoadingDetail || !projectDetail))) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={title}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
        // permissionsBasic={'ing-projects'}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock title={t('Inž. projekt')} collapsible={false}>
          <IngProjectsForm
            initials={initials}
            form={form}
            studyProgramsOptions={studyProgramsOptions}
            teachersOptions={teachersOptions}
            additionalTeachersOptions={additionalTeachersOptions}
            disableGuarantorSelection={mustUseOwnGuarantor}
            onSubmit={(values) => {
              const guarantorId = forcedGuarantorId ?? (isCreate
                ? currentTeacherId
                : (values.guarantor_id ?? projectDetail?.guarantor_id));

              const payload = {
                ...values,
                guarantor_id: guarantorId,
              };

              if (isEdit) {
                saveIngProject({ projectId: projectId, body: payload });
              } else {
                saveIngProject({ body: payload });
              }
            }}
            loading={isSaving}
            disabled={!canManageProjects}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default IngProjectsEdit;
